import { useState, useEffect, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { 
  Users, 
  Brain, 
  BarChart3, 
  MessageSquare, 
  Star,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  CheckCircle,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  Sparkles,
  Shield,
  Pencil,
  Check,
  Plus,
  BookOpenCheck,
  Sprout
} from 'lucide-react'
import { feedbackService } from '@/services/api/feedbackService'
import { aiService, type FeedbackAnalysisResponse } from '@/services/api/aiService'
import { integrationService } from '@/services/api/integrationService'
import { idpService } from '@/services/api/idpService'
import type { FeedbackRequest, FeedbackResponseIntegrationStatus } from '@/services/api/feedbackService'
import FeedbackEditDialog from './FeedbackEditDialog'
import { useAuth } from '@/providers/AuthProvider'
import toast from 'react-hot-toast'

interface FeedbackDetailViewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  requestId: string
  onRefresh?: () => void
}

export default function FeedbackDetailView({
  open,
  onOpenChange,
  requestId,
  onRefresh
}: FeedbackDetailViewProps) {
  const [request, setRequest] = useState<FeedbackRequest | null>(null)
  const [responsesData, setResponsesData] = useState<Record<string, any>>({})
  const [loading, setLoading] = useState(false)
  const [aiAnalysis, setAiAnalysis] = useState<FeedbackAnalysisResponse | null>(null)
  const [loadingAI, setLoadingAI] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [employeeName, setEmployeeName] = useState<string>('')
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const { user } = useAuth()
  const aiAnalysisCalledRef = useRef(false)

  // #31: per-response integration state, keyed by feedback response_id.
  //   status:  whether the response is already in portfolio / idp / pl-obs
  //   busy:    which action ('portfolio' | 'idp' | 'pl') is in flight
  const [integrationStatus, setIntegrationStatus] = useState<
    Record<string, FeedbackResponseIntegrationStatus>
  >({})
  const [integrationBusy, setIntegrationBusy] = useState<
    Record<string, 'portfolio' | 'idp' | 'pl' | null>
  >({})
  // PL-observation add state is local (no portfolio-status field for it):
  // remember which responses the PL has filed this session.
  const [plObservationAdded, setPlObservationAdded] = useState<Record<string, boolean>>({})
  // June 2026: PL "add this PL-initiated feedback to the team member's Impact
  // Portfolio" — request-level (one entry per request). null = not yet checked.
  const [requestInPortfolio, setRequestInPortfolio] = useState<boolean | null>(null)
  const [addingRequestToPortfolio, setAddingRequestToPortfolio] = useState(false)

  // Viewer roles relative to this request.
  const isSubject = !!user?.employeeId && request?.requested_for_user_id === user.employeeId
  const isRequester = !!user?.employeeId && request?.requested_by_user_id === user.employeeId

  useEffect(() => {
    if (open && requestId) {
      aiAnalysisCalledRef.current = false // Reset on open
      loadRequest()
    }
  }, [open, requestId])

  const loadRequest = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await feedbackService.getFeedbackRequest(requestId)
      setRequest(data)
      
      // Fetch employee name
      try {
        const employee = await integrationService.getEmployeeByPersonnelNumber(data.requested_for_user_id)
        if (employee) {
          setEmployeeName(employee.full_name)
        } else {
          setEmployeeName(data.requested_for_user_id) // Fallback to ID
        }
      } catch (err) {
        console.error('Failed to fetch employee name:', err)
        setEmployeeName(data.requested_for_user_id) // Fallback to ID
      }
      
      // Fetch actual response data for each stakeholder with a response
      const responsesMap: Record<string, any> = {}
      for (const stakeholder of data.stakeholders) {
        if (stakeholder.response_id) {
          try {
            const responseData = await feedbackService.getFeedbackResponse(stakeholder.response_id)
            responsesMap[stakeholder.response_id] = responseData
          } catch (err) {
            console.error(`Failed to load response ${stakeholder.response_id}:`, err)
          }
        }
      }
      setResponsesData(responsesMap)

      // June 2026: for the people leader (requester, not the subject), check
      // whether this feedback is already mirrored into the team member's Impact
      // Portfolio so the "Add to … Impact Portfolio" button reflects state.
      if (user?.employeeId
          && data.requested_by_user_id === user.employeeId
          && data.requested_for_user_id !== user.employeeId) {
        try {
          const st = await feedbackService.getRequestPortfolioStatus(requestId)
          setRequestInPortfolio(st.in_portfolio)
        } catch (err) {
          console.error('Failed to load request portfolio status:', err)
          setRequestInPortfolio(false)
        }
      }

      // Load AI analysis if enabled (prevent duplicate calls in StrictMode)
      if (data.ai_analysis_enabled && (data.responses_received ?? 0) > 0 && !aiAnalysisCalledRef.current) {
        aiAnalysisCalledRef.current = true
        loadAIAnalysis(data, responsesMap)
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load feedback request')
    } finally {
      setLoading(false)
    }
  }

  // #31: fetch integration status for a set of response ids in parallel.
  const loadIntegrationStatuses = async (responseIds: string[]) => {
    if (responseIds.length === 0) return
    const entries = await Promise.all(
      responseIds.map(async (id) => {
        try {
          const status = await feedbackService.getResponsePortfolioStatus(id)
          return [id, status] as const
        } catch (err) {
          console.error(`Failed to load portfolio status for ${id}:`, err)
          return null
        }
      })
    )
    setIntegrationStatus((prev) => {
      const next = { ...prev }
      for (const entry of entries) {
        if (entry) next[entry[0]] = entry[1]
      }
      return next
    })
  }

  // #31: add a single completed response to the subject's Impact Portfolio.
  const handleAddToPortfolio = async (responseId: string) => {
    setIntegrationBusy((prev) => ({ ...prev, [responseId]: 'portfolio' }))
    try {
      const result = await feedbackService.addResponseToPortfolio(responseId)
      setIntegrationStatus((prev) => ({
        ...prev,
        [responseId]: {
          ...(prev[responseId] || { in_idp: false, idp_strength_id: null }),
          in_portfolio: true,
          portfolio_evidence_id: result.evidence_id,
        },
      }))
      toast.success(result.already_added ? 'Already in your Impact Portfolio' : 'Added to your Impact Portfolio')
    } catch (err: any) {
      console.error('Failed to add to portfolio:', err)
      toast.error(err.response?.data?.detail || 'Could not add to your portfolio')
    } finally {
      setIntegrationBusy((prev) => ({ ...prev, [responseId]: null }))
    }
  }

  // #31: add a single completed response to the subject's IDP as a strength.
  // The add-to-idp endpoint needs an idp_id — resolve the subject's current
  // IDP (most recent) before posting.
  const handleAddToIdp = async (responseId: string) => {
    if (!user?.employeeId) return
    setIntegrationBusy((prev) => ({ ...prev, [responseId]: 'idp' }))
    try {
      const list = await idpService.getUserIDPs(user.employeeId)
      const idp = list.idps?.[0]
      if (!idp?.id) {
        toast.error('No Personalised Growth plan found yet — create one first.')
        return
      }
      const result = await feedbackService.addResponseToIdp(responseId, idp.id, 'strength')
      setIntegrationStatus((prev) => ({
        ...prev,
        [responseId]: {
          ...(prev[responseId] || { in_portfolio: false, portfolio_evidence_id: null }),
          in_idp: true,
          idp_strength_id: result.strength_id,
        },
      }))
      toast.success(
        result.already_added ? 'Already in your Personalised Growth' : 'Added to your Personalised Growth'
      )
    } catch (err: any) {
      console.error('Failed to add to IDP:', err)
      toast.error(err.response?.data?.detail || 'Could not add to your Personalised Growth')
    } finally {
      setIntegrationBusy((prev) => ({ ...prev, [responseId]: null }))
    }
  }

  // #31 (PL side): file a completed response into the PL's private
  // observations about the team member.
  const handleAddToPlObservations = async (responseId: string) => {
    if (!request?.requested_for_user_id) return
    setIntegrationBusy((prev) => ({ ...prev, [responseId]: 'pl' }))
    try {
      const result = await feedbackService.addResponseToPlObservations(
        responseId,
        request.requested_for_user_id
      )
      setPlObservationAdded((prev) => ({ ...prev, [responseId]: true }))
      toast.success(result.already_added ? 'Already in your observations' : 'Added to your observations')
    } catch (err: any) {
      console.error('Failed to add to PL observations:', err)
      toast.error(err.response?.data?.detail || 'Could not add to your observations')
    } finally {
      setIntegrationBusy((prev) => ({ ...prev, [responseId]: null }))
    }
  }

  // June 2026 (PL side): share this PL-initiated feedback into the team
  // member's Impact Portfolio as a single read-only entry. Idempotent.
  const handleAddRequestToPortfolio = async () => {
    if (!requestId) return
    setAddingRequestToPortfolio(true)
    try {
      const result = await feedbackService.addRequestToPortfolio(requestId)
      setRequestInPortfolio(true)
      const who = employeeName || 'the team member'
      toast.success(
        result.already_added ? `Already in ${who}'s Impact Portfolio` : `Added to ${who}'s Impact Portfolio`,
      )
    } catch (err: any) {
      console.error('Failed to add request to portfolio:', err)
      toast.error(err.response?.data?.detail || 'Could not add this feedback to the portfolio')
    } finally {
      setAddingRequestToPortfolio(false)
    }
  }

  const loadAIAnalysis = async (requestData: FeedbackRequest, responsesMap: Record<string, any>) => {
    if (!requestData.stakeholders || requestData.stakeholders.length === 0) {
      return
    }

    try {
      setLoadingAI(true)
      
      // Build responses with actual answer data
      const responses = requestData.stakeholders
        .filter(s => s.response_status === 'completed' && s.response_id)
        .map(s => {
          const responseData = responsesMap[s.response_id!]
          return {
            stakeholder_name: requestData.is_confidential ? 'Anonymous' : s.stakeholder_name,
            stakeholder_role: s.stakeholder_role,
            category: s.category,
            responses: responseData?.response_data || [],
            overall_rating: responseData?.overall_rating,
            general_comments: responseData?.general_comments
          }
        })

      if (responses.length === 0) {
        return
      }

      const analysis = await aiService.analyzeFeedback({
        feedback_request_id: requestData.id!,
        requested_for_name: requestData.requested_for_user_id,
        request_title: requestData.title,
        responses,
        is_confidential: requestData.is_confidential
      })

      setAiAnalysis(analysis)
    } catch (err: any) {
      console.error('Failed to load AI analysis:', err)
      toast.error('AI analysis unavailable')
    } finally {
      setLoadingAI(false)
    }
  }

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600 bg-green-50'
      case 'negative': return 'text-red-600 bg-red-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return <ThumbsUp className="h-4 w-4" />
      case 'negative': return <ThumbsDown className="h-4 w-4" />
      default: return <MessageSquare className="h-4 w-4" />
    }
  }

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[95vw] max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Loading Feedback</DialogTitle>
            <DialogDescription>Please wait while we load the feedback details</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-heritage-blue mr-3" />
            <span>Loading feedback details...</span>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  if (error || !request) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[95vw] max-w-6xl">
          <DialogHeader>
            <DialogTitle>Error Loading Feedback</DialogTitle>
            <DialogDescription>Unable to load the feedback details</DialogDescription>
          </DialogHeader>
          <div className="text-center py-12">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <p className="text-red-600">{error || 'Feedback request not found'}</p>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  const completedResponses = request.stakeholders?.filter(s => s.response_status === 'completed') || []
  const pendingResponses = request.stakeholders?.filter(s => s.response_status === 'pending') || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-3">
              <span>{request.title}</span>
              {request.is_confidential && (
                <Badge variant="secondary" className="bg-purple-50 text-purple-700">
                  <Shield className="h-3 w-3 mr-1" />
                  Confidential
                </Badge>
              )}
            </DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditDialogOpen(true)}
              className="ml-4"
            >
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </div>
          <DialogDescription>
            Feedback for {employeeName || request.requested_for_user_id} • {completedResponses.length} of {request.total_stakeholders} responses received
          </DialogDescription>
        </DialogHeader>

        {/* Edit Dialog */}
        <FeedbackEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          request={request}
          onUpdate={() => {
            loadRequest()
            if (onRefresh) onRefresh()
          }}
          currentUserId={user?.employeeId}
        />

        {/* June 2026 rework: the whole feedback is mirrored automatically into
            the subject's Impact Portfolio (one read-only entry per request,
            refreshed as responses arrive) — no per-response cherry-picking. It
            stays private until the team member shares it with their people
            leader. The people leader still files individual responses into their
            own private observations. */}
        {completedResponses.length > 0 && (isSubject || isRequester) && (
          <div className="my-3 rounded-lg border border-[#E7E5DB] bg-[#FAFAF7] px-4 py-3">
            <p className="text-sm font-medium text-slate-800">
              {isSubject ? 'Saved to your Impact Portfolio' : 'File these observations'}
            </p>
            <p className="text-xs text-slate-600 mt-0.5">
              {isSubject
                ? 'This feedback is automatically kept in your Impact Portfolio as a single, read-only entry. It stays private to you until you share it with your people leader — sharing is how you choose what they see.'
                : 'Open the Responses tab to add any response to your private observations — or add the whole feedback to this team member’s Impact Portfolio below.'}
            </p>
            {/* PL action: share this PL-initiated feedback into the team
                member's Impact Portfolio (one read-only entry per request).
                Self-requested feedback mirrors automatically and shows no
                button here. */}
            {isRequester && !isSubject && (
              <div className="mt-3">
                {requestInPortfolio ? (
                  <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">
                    <Check className="h-3 w-3 mr-1" />
                    Added to {employeeName || 'the team member'}’s Impact Portfolio
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={addingRequestToPortfolio}
                    onClick={handleAddRequestToPortfolio}
                  >
                    {addingRequestToPortfolio ? (
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                      <BookOpenCheck className="h-4 w-4 mr-1.5" />
                    )}
                    Add to {employeeName || 'the team member'}’s Impact Portfolio
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="grid w-full grid-cols-3 text-xs sm:text-sm">
            <TabsTrigger value="summary">
              <BarChart3 className="h-4 w-4 mr-2" />
              Summary
            </TabsTrigger>
            <TabsTrigger value="responses">
              <MessageSquare className="h-4 w-4 mr-2" />
              Responses ({completedResponses.length})
            </TabsTrigger>
            <TabsTrigger value="ai-insights" disabled={!request.ai_analysis_enabled || completedResponses.length === 0}>
              <Brain className="h-4 w-4 mr-2" />
              AI Insights
              {request.ai_analysis_enabled && <Sparkles className="h-3 w-3 ml-1" />}
            </TabsTrigger>
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                    <div className="text-2xl font-bold">{request.total_stakeholders}</div>
                    <p className="text-sm text-gray-600">Total Stakeholders</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-600" />
                    <div className="text-2xl font-bold">{completedResponses.length}</div>
                    <p className="text-sm text-gray-600">Completed</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 text-amber-600" />
                    <div className="text-2xl font-bold">{pendingResponses.length}</div>
                    <p className="text-sm text-gray-600">Pending</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Request Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Request Type</p>
                    <p className="font-medium">{request.request_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <Badge>{request.status}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Recommended due date</p>
                    <p className="font-medium">
                      {request.due_date ? new Date(request.due_date).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Created</p>
                    <p className="font-medium">
                      {new Date(request.created_at!).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                {request.description && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Description</p>
                    <p className="text-sm">{request.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Stakeholders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {request.stakeholders?.map((stakeholder, idx) => (
                    <div key={stakeholder.id || idx} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">
                          {request.is_confidential ? `Stakeholder ${idx + 1}` : stakeholder.stakeholder_name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {stakeholder.category} • {stakeholder.stakeholder_role || 'N/A'}
                        </p>
                      </div>
                      <Badge variant={stakeholder.response_status === 'completed' ? 'default' : 'secondary'}>
                        {stakeholder.response_status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Responses Tab */}
          <TabsContent value="responses" className="space-y-4">
            {completedResponses.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-600">No responses received yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* June 2026 rework: only the People Leader files responses here.
                    The subject's own feedback is mirrored into their Impact
                    Portfolio automatically (one read-only entry), so they have
                    no per-response actions to take. */}
                {(isRequester && !isSubject) && (
                  <Card className="border-[#E7E5DB] bg-[#FAFAF7]">
                    <CardHeader>
                      <CardTitle className="text-base">File responses to your observations</CardTitle>
                      <CardDescription>
                        These observations stay private to you as this team member’s people leader.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {completedResponses.map((stakeholder, idx) => {
                        const responseId = stakeholder.response_id
                        if (!responseId) return null
                        const status = integrationStatus[responseId]
                        const busy = integrationBusy[responseId]
                        const submitted = stakeholder.response_submitted_at
                          ? new Date(stakeholder.response_submitted_at).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })
                          : null
                        const respondentLabel = request.is_confidential
                          ? `Respondent ${idx + 1}`
                          : stakeholder.stakeholder_name
                        return (
                          <div
                            key={stakeholder.id || responseId}
                            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#E7E5DB] bg-white px-4 py-3"
                          >
                            <div className="min-w-[160px]">
                              <p className="text-sm font-medium text-slate-800">{respondentLabel}</p>
                              {submitted && (
                                <p className="text-xs text-slate-500">Submitted {submitted}</p>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              {/* PL side: only when the viewer is the requester and NOT the subject */}
                              {isRequester && !isSubject && (
                                plObservationAdded[responseId] ? (
                                  <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">
                                    <Check className="h-3 w-3 mr-1" />
                                    Added to observations
                                  </Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={!!busy}
                                    onClick={() => handleAddToPlObservations(responseId)}
                                  >
                                    {busy === 'pl' ? (
                                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                    ) : (
                                      <Plus className="h-4 w-4 mr-1.5" />
                                    )}
                                    Add to PL Observations
                                  </Button>
                                )
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </CardContent>
                  </Card>
                )}

                {/* Get questions from: 1) custom_questions, 2) template questions, 3) extract from responses */}
                {(() => {
                  // Priority 1: Use custom_questions if available
                  if (request.custom_questions && request.custom_questions.length > 0) {
                    return request.custom_questions
                  }
                  
                  // Priority 2: Use template questions if available
                  if (request.question_template?.template_questions) {
                    return request.question_template.template_questions
                      .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0))
                      .map((tq: any) => ({
                        id: tq.question?.id || tq.question_id,
                        text: tq.custom_question_text || tq.question?.question_text,
                        help_text: tq.custom_helper_text || tq.question?.helper_text,
                        type: tq.question?.question_type,
                        required: tq.is_required
                      }))
                  }
                  
                  // Priority 3: Extract unique questions from response data
                  const uniqueQuestions = new Map<string, any>()
                  Object.values(responsesData).forEach((responseObj: any) => {
                    if (responseObj?.response_data) {
                      responseObj.response_data.forEach((r: any) => {
                        if (r.question_id && !uniqueQuestions.has(r.question_id)) {
                          uniqueQuestions.set(r.question_id, {
                            id: r.question_id,
                            text: r.question_text,
                            type: r.response_type,
                            required: true
                          })
                        }
                      })
                    }
                  })
                  return Array.from(uniqueQuestions.values())
                })().map((question: any, qIdx: number) => (
                  <Card key={question.id || qIdx}>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Q{qIdx + 1}: {question.text}
                      </CardTitle>
                      {question.help_text && (
                        <CardDescription>{question.help_text}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {completedResponses.map((stakeholder, sIdx) => {
                        const responseData = stakeholder.response_id ? responsesData[stakeholder.response_id] : null
                        
                        // Match response by question_id or question_text only
                        // DO NOT use index-based matching - it causes misalignment when users skip questions
                        let answer = null
                        if (responseData?.response_data) {
                          const responses = responseData.response_data
                          // 1. Try matching by question_id
                          const byId = responses.find((r: any) => r.question_id === question.id)
                          if (byId?.answer !== undefined && byId?.answer !== null) {
                            answer = byId.answer
                          } else {
                            // 2. Try matching by question text (handles template questions with different IDs)
                            const byText = responses.find((r: any) => 
                              r.question_text && question.text && 
                              r.question_text.toLowerCase().trim() === question.text.toLowerCase().trim()
                            )
                            if (byText?.answer !== undefined && byText?.answer !== null) {
                              answer = byText.answer
                            }
                            // NO index-based fallback - if question was skipped, show "Question skipped"
                          }
                        }
                        
                        return (
                          <div key={stakeholder.id || sIdx} className="border-l-4 border-blue-200 pl-4 py-2">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-sm">
                                {request.is_confidential ? `Respondent ${sIdx + 1}` : stakeholder.stakeholder_name}
                              </p>
                              <Badge variant="outline" className="text-xs">
                                {stakeholder.category}
                              </Badge>
                            </div>
                            <p className={`text-sm ${answer ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                              {!responseData ? 'Loading response...' : (answer || 'Question not answered')}
                            </p>
                          </div>
                        )
                      })}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* AI Insights Tab */}
          <TabsContent value="ai-insights" className="space-y-4">
            {loadingAI ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-heritage-blue" />
                  <p className="text-gray-600">Generating AI insights...</p>
                  <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
                </CardContent>
              </Card>
            ) : !aiAnalysis ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Brain className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-600 mb-4">AI analysis not available</p>
                  {request.ai_analysis_enabled && (
                    <Button onClick={() => request && loadAIAnalysis(request, responsesData)}>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate AI Insights
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Executive Summary */}
                <Card className="border-l-4 border-heritage-blue">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-heritage-blue" />
                      Executive Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{aiAnalysis.summary}</p>
                  </CardContent>
                </Card>

                {/* Key Themes */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Key Themes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {aiAnalysis.key_themes.map((theme, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {getSentimentIcon(theme.sentiment)}
                            <h4 className="font-semibold">{theme.theme}</h4>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{theme.frequency} mentions</Badge>
                            <Badge className={getSentimentColor(theme.sentiment)}>
                              {theme.sentiment}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{theme.description}</p>
                        {theme.examples.length > 0 && (
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs text-gray-600 mb-1">Examples:</p>
                            <ul className="text-xs text-gray-600 list-disc list-inside">
                              {theme.examples.map((example, eIdx) => (
                                <li key={eIdx}>{example}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Strengths */}
                  <Card className="border-l-4 border-green-500">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-green-700">
                        <ThumbsUp className="h-5 w-5" />
                        Strengths
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {aiAnalysis.strengths.map((strength, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Star className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{strength}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Development Areas */}
                  <Card className="border-l-4 border-amber-500">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-amber-700">
                        <TrendingUp className="h-5 w-5" />
                        Development Areas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {aiAnalysis.development_areas.map((area, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{area}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                {/* Recommendations */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-amber-500" />
                      Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {aiAnalysis.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                          <span className="font-semibold text-amber-700">{idx + 1}.</span>
                          <span className="text-sm text-gray-700">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
